import {
    createDefaultChildFeatures,
    decodeChildFeatures,
    type ChildFeatures
} from '@tadpoles/common'

import { deduplicateBy } from '../utils/deduplicate-by.js'
import { isTadpolesMode } from '../utils/feature/is-tadpoles-mode.js'
import type { FamilyUserLocalBacking } from './persistence/family-local-backing.js'
import { replacePersistedCollection } from './persistence/replace-persisted-collection.js'
import type { FeatureAssignment } from './persistence/stores/feature-store.js'
import type { ServiceLifecycle } from './service-lifecycle.js'
import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

type DecodedFeatureAssignment = {
    childKey: string
    features: ChildFeatures
}

export type FamilyLibraryEntitlements = {
    readonly ebook: boolean
    readonly enabled: boolean
    readonly video: boolean
}

export class FeatureService extends UserScopedService implements ServiceLifecycle {
    readonly #localBacking: FamilyUserLocalBacking
    readonly #restoreFromBacking: boolean
    #assignments = $state.raw<FeatureAssignment[]>([])
    #decodedAssignments = $derived<DecodedFeatureAssignment[]>(
        this.#assignments.map(({ childKey, features }) => ({
            childKey,
            features: decodeChildFeatures(features)
        }))
    )
    #anyChildHasTadpoles = $derived(
        this.#decodedAssignments.some(({ features }) => isTadpolesMode(features.tsg_mode))
    )
    #libraryEntitlements = $derived.by<FamilyLibraryEntitlements>(() => {
        let ebook = false
        let video = false

        for (const { features } of this.#decodedAssignments) {
            ebook ||= features.library_ebooks
            video ||= features.library_videos
        }

        return Object.freeze({ ebook, enabled: ebook || video, video })
    })
    public constructor(
        scope: UserScopedServiceScope,
        localBacking: FamilyUserLocalBacking,
        restoreFromBacking = true
    ) {
        super(scope)
        this.#localBacking = localBacking
        this.#restoreFromBacking = restoreFromBacking
    }

    public get assignments(): FeatureAssignment[] {
        return this.#assignments
    }

    public get anyChildHasTadpoles(): boolean {
        return this.#anyChildHasTadpoles
    }

    public get libraryEntitlements(): FamilyLibraryEntitlements {
        return this.#libraryEntitlements
    }

    public async initialize(): Promise<void> {
        if (!this.#restoreFromBacking) {
            return
        }

        const store = await this.#localBacking.getFeatureStore()
        this.hydrate(store === undefined ? [] : await store.findAll())
    }

    public resume(): void {}

    public suspend(): void {}

    public destroy(): void {
        this.hydrate([])
    }

    public hydrate(assignments: FeatureAssignment[]): void {
        const nextAssignments = deduplicateBy(assignments, (assignment) => assignment.childKey)
        this.#reportInvalidValues(nextAssignments)
        this.#assignments = nextAssignments
    }

    public replaceAll(assignments: FeatureAssignment[]): Promise<void> {
        this.hydrate(assignments)
        const snapshot = this.#assignments

        return this.enqueueCacheWrite(async () => {
            const store = await this.#localBacking.getFeatureStore()

            if (store !== undefined) {
                await replacePersistedCollection(
                    store,
                    snapshot,
                    (assignment) => assignment.childKey
                )
            }
        })
    }

    public getForChild(childKey: string | undefined): ChildFeatures {
        if (childKey === undefined) {
            return createDefaultChildFeatures()
        }

        return (
            this.#decodedAssignments.find((assignment) => assignment.childKey === childKey)
                ?.features ?? createDefaultChildFeatures()
        )
    }

    #reportInvalidValue(childKey: string, identifier: string): void {
        console.warn('Unable to decode child feature value; using its default.', {
            childKey,
            identifier
        })
    }

    #reportInvalidValues(assignments: FeatureAssignment[]): void {
        for (const { childKey, features } of assignments) {
            decodeChildFeatures(features, {
                onInvalidValue: (feature) => this.#reportInvalidValue(childKey, feature.identifier)
            })
        }
    }
}
